import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigation,
} from "react-router";

import type { Route } from "./+types/root";
import stylesheet from "./app.css?url";
import PageLoader from "./components/page-loader";
import { Provider } from "react-redux";
import {
  THEME_KEY,
  ThemeProvider,
} from "./components/providers/theme-provider";
import { store } from "./redux/store";
import { Button } from "./components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Card } from "./components/ui/card";
import LogoText from "./components/logo/LogoText";
import { Toaster } from "./components/ui/toaster";
import { cn } from "./lib/utils";
import { getCookie } from "./hooks/cookie-helper";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  { rel: "stylesheet", href: stylesheet },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <Toaster />
      </body>
    </html>
  );
}

export function loader({ request }: Route.LoaderArgs) {
  let cookiedTheme = request.headers.get("cookie");
  cookiedTheme =
    !cookiedTheme || cookiedTheme.trim() === "" ? "system" : cookiedTheme;
  const theme = getCookie(THEME_KEY, cookiedTheme ?? "system");
  return { theme };
}

export default function App({ loaderData: { theme } }: any) {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  return (
    <Provider store={store}>
      <ThemeProvider defaultTheme={theme}>
        {isNavigating && <PageLoader />}
        <div className={cn(isNavigating ? "hidden" : "block")}>
          <Outlet />
        </div>
      </ThemeProvider>
    </Provider>
  );
}

function ErrorContent({ error }: { error: any }) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The page you're looking for seems to have vanished into thin air."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8 space-y-6 text-center">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold">
            <LogoText />
          </h1>
        </div>

        {/* Error Content */}
        <div className="space-y-4">
          <h2 className="text-6xl font-bold text-[#1A365D] dark:text-gray-100">
            {message}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg">{details}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleGoBack}
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
          <Button
            variant="default"
            className="bg-[#3182CE] hover:bg-[#2B6CB0] dark:bg-[#63B3ED] dark:hover:bg-[#4299E1]"
            onClick={() => (window.location.href = "/")}
          >
            Return Home
          </Button>
        </div>

        {/* Dev Stack Trace */}
        {stack && (
          <div className="mt-8">
            <pre className="w-full p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-left overflow-x-auto text-sm">
              <code>{stack}</code>
            </pre>
          </div>
        )}
      </Card>
    </div>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  return <ErrorContent error={error} />;
}
